import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Mountain } from "lucide-react";

const LINKS = [
  { to: "/hikes", label: "The Hikes" },
  { to: "/about", label: "About Vasilis" },
  { to: "/contact", label: "Contact" },
];

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const darkHero = pathname === "/" || /^\/hikes\/[^/]+$/.test(pathname);
  const onDark = darkHero && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "bg-paper/80 border-ink/10" : "bg-paper/0 border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-2">
          <span className={`font-serif text-3xl font-medium tracking-tight transition-colors duration-500 ${onDark ? "text-paper [text-shadow:0_1px_12px_rgba(15,23,42,0.6)]" : "text-ink"}`}>
            Salt <span className="text-4xl italic text-terra">&amp;</span> Stone
          </span>
          <span className={`hidden text-sm uppercase tracking-[0.3em] transition-colors duration-500 sm:inline ${onDark ? "text-paper/70" : "text-ink/50"}`}>
            Hydra · Greece
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-base uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive
                    ? "text-terra"
                    : onDark
                      ? "text-paper/85 [text-shadow:0_1px_10px_rgba(15,23,42,0.55)] hover:text-paper"
                      : "text-ink/70 hover:text-terra"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/hikes"
            data-testid="nav-book-btn"
            className="rounded-full bg-terra px-6 py-2.5 text-base uppercase tracking-[0.15em] text-paper transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-terra-dark"
          >
            Book a hike
          </Link>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`transition-colors duration-500 md:hidden ${onDark ? "text-paper" : "text-ink"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-ink/10 md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 font-serif text-3xl tracking-tight text-ink"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/hikes"
                data-testid="nav-mobile-book-btn"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-terra px-6 py-4 text-base uppercase tracking-[0.15em] text-paper"
              >
                <Mountain size={16} /> Book a hike
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
